import React, { useEffect, useRef, useState } from 'react';
import propTypes from 'prop-types';
import Chart from 'chart.js/auto';
import dayjs from 'dayjs';
import useRequest from '../../../Hooks/useRequest';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import styled from 'styled-components';

const StyledTableContainer = styled.div`
    margin-top: 40px;
`;
const ViewCampaignAnalyticsProgrammatic = ({ campaignType, campaignId }) => {
    const chartContainerRefs = useRef([]);

    const [statData, setStatData] = useState({});

    const { data: pontiacCampaignStatsData } = useRequest(
        `/GetPontiacCampaignStatsByCampaignId/${campaignId}`
    );

    useEffect(() => {
        setStatData(pontiacCampaignStatsData);
    }, [pontiacCampaignStatsData]);

    const { campaignName, dailyStats } = { ...statData };

    const totalRows = [statData];

    const dailyStatDates = dailyStats?.map((item) => dayjs(item.statsDate).format('MM/DD/YYYY'));
    const dailyStatImps = dailyStats?.map((item) => item.totalImps);
    const dailyStatClicks = dailyStats?.map((item) => item.totalClicks);
    const dailyStatCPM = dailyStats?.map((item) =>
        ((item.totalSpend / item.totalImps) * 1000).toFixed(2)
    );
    const dailyStatCTR = dailyStats?.map((item) =>
        ((item.totalClicks / item.totalImps) * 100).toFixed(2)
    );

    useEffect(() => {
        const chartContainers = chartContainerRefs.current;

        const lineDataImps = {
            labels: dailyStatDates,
            datasets: [
                {
                    label: 'Line Chart',
                    data: dailyStatImps,
                    fill: false,
                    borderColor: 'blue',
                },
            ],
        };

        const lineDataClicks = {
            labels: dailyStatDates,
            datasets: [
                {
                    label: 'Line Chart',
                    data: dailyStatClicks,
                    fill: false,
                    borderColor: 'blue',
                },
            ],
        };

        const lineDataCPM = {
            labels: dailyStatDates,
            datasets: [
                {
                    label: 'Line Chart',
                    data: dailyStatCPM,
                    fill: false,
                    borderColor: 'blue',
                },
            ],
        };

        const lineDataCTR = {
            labels: dailyStatDates,
            datasets: [
                {
                    label: 'Line Chart',
                    data: dailyStatCTR,
                    fill: false,
                    borderColor: 'blue',
                },
            ],
        };

        // Create separate charts for each canvas element

        const lineChartImps = new Chart(chartContainers[0]?.getContext('2d'), {
            type: 'line',
            data: lineDataImps,
            options: {
                plugins: {
                    title: {
                        display: true,
                        text: 'Daily Impressions',
                        font: {
                            size: '30px',
                        },
                    },
                },
            },
        });

        const lineChartClicks = new Chart(chartContainers[1]?.getContext('2d'), {
            type: 'line',
            data: lineDataClicks,
            options: {
                plugins: {
                    title: {
                        display: true,
                        text: 'Daily Clicks',
                        font: {
                            size: '30px',
                        },
                    },
                },
            },
        });

        const lineChartCPM = new Chart(chartContainers[2]?.getContext('2d'), {
            type: 'line',
            data: lineDataCPM,
            options: {
                plugins: {
                    title: {
                        display: true,
                        text: 'Daily Cost Per Thousand Impressions (CPM)',
                        font: {
                            size: '30px',
                        },
                    },
                },
            },
        });

        const lineChartCTR = new Chart(chartContainers[3]?.getContext('2d'), {
            type: 'line',
            data: lineDataCTR,
            options: {
                plugins: {
                    title: {
                        display: true,
                        text: 'Daily Click Through Rate (CTR)',
                        font: {
                            size: '30px',
                        },
                    },
                },
            },
        });

        // Cleanup: Destroy charts when component unmounts
        return () => {
            lineChartClicks.destroy();
            lineChartImps.destroy();
            lineChartCPM.destroy();
            lineChartCTR.destroy();
        };
    }, [statData]);

    return (
        <div
            style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div
                style={{
                    textAlign: 'center',
                    fontSize: '23px',
                    fontWeight: 'bold',
                    marginTop: '50px',
                }}
            >
                Analytics
            </div>

            <div
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '120px',
                    flexDirection: 'column',
                }}
            >
                <StyledTableContainer>
                    <div>
                        <div
                            style={{
                                width: '100%',
                                maxWidth: '1500px',
                                display: 'table',
                                tableLayout: 'fixed',
                            }}
                        >
                            <TableContainer
                                style={{
                                    border: '1px solid black',
                                    borderRadius: '12px',
                                    backgroundColor: ' #fafbf7',
                                }}
                                component={Paper}
                            >
                                <Table aria-label="simple table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Campaign Name</TableCell>
                                            <TableCell align="right">Campaign Type</TableCell>
                                            <TableCell align="right">Campaign Range</TableCell>
                                            <TableCell align="right"># of Days</TableCell>

                                            <TableCell align="right">Total Impressions</TableCell>
                                            <TableCell align="right">Total Clicks</TableCell>
                                            <TableCell align="right">Total Budget</TableCell>
                                            <TableCell align="right">Total CPM</TableCell>
                                            <TableCell align="right">Total CTR</TableCell>
                                            <TableCell align="right">Total CPC</TableCell>
                                            <TableCell align="right">Updated By</TableCell>
                                            <TableCell align="right">Updated On</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {totalRows?.map((row, idx) => (
                                            <TableRow
                                                key={idx}
                                                sx={{
                                                    '&:last-child td, &:last-child th': {
                                                        border: 0,
                                                    },
                                                }}
                                            >
                                                <TableCell component="th" scope="row">
                                                    {campaignName}
                                                </TableCell>
                                                <TableCell align="right">{campaignType}</TableCell>
                                                <TableCell align="right">
                                                    {`${dayjs(row?.startDate).format(
                                                        'MM/DD/YYYY'
                                                    )}- ${dayjs(row?.endDate).format(
                                                        'MM/DD/YYYY'
                                                    )}`}
                                                </TableCell>
                                                <TableCell align="right">{row?.dayCount}</TableCell>
                                                <TableCell align="right">
                                                    {row?.totalImps?.toLocaleString()}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {row?.totalClicks?.toLocaleString()}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {row?.budget?.toLocaleString('en-US', {
                                                        style: 'currency',
                                                        currency: 'USD',
                                                    })}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {row?.totalSpend && row?.totalImps
                                                        ? (
                                                              (row?.totalSpend / row?.totalImps) *
                                                              1000
                                                          ).toLocaleString()
                                                        : 0}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {row?.totalClicks && row?.totalImps
                                                        ? (
                                                              (row?.totalClicks / row?.totalImps) *
                                                              100
                                                          ).toLocaleString()
                                                        : 0}
                                                    %
                                                </TableCell>
                                                <TableCell align="right">
                                                    {row?.totalSpend && row?.totalClicks
                                                        ? (
                                                              row?.totalSpend / row?.totalClicks
                                                          )?.toLocaleString('en-US', {
                                                              style: 'currency',
                                                              currency: 'USD',
                                                          })
                                                        : 0}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {row?.updatedBy}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {dayjs(row?.updatedOn).format('MM/DD/YYYY')}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                    </div>
                </StyledTableContainer>
                {totalRows.length === 0 || totalRows[0] === undefined ? (
                    <div
                        style={{
                            textAlign: 'center',
                            backgroundColor: 'white',
                        }}
                    >
                        There are no daily statistics for this campaign.
                    </div>
                ) : null}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div style={{ width: '1500px' }}>
                    <div>
                        <canvas ref={(el) => (chartContainerRefs.current[0] = el)} />
                        <canvas ref={(el) => (chartContainerRefs.current[1] = el)} />
                    </div>

                    <div>
                        <canvas ref={(el) => (chartContainerRefs.current[2] = el)} />
                        <canvas ref={(el) => (chartContainerRefs.current[3] = el)} />
                    </div>
                    <div>
                        <canvas ref={(el) => (chartContainerRefs.current[5] = el)} />
                    </div>
                </div>
            </div>
        </div>
    );
};

ViewCampaignAnalyticsProgrammatic.propTypes = {
    campaignType: propTypes.string,
    campaignId: propTypes.string,
};

export default ViewCampaignAnalyticsProgrammatic;
